// ===========================================================================
// ReHome — Utókövetési helyzetjelentés (check-in) ütemezés
// ---------------------------------------------------------------------------
// Egyetlen forrás arra a kérdésre, hogy egy örökbefogadott állathoz ESEDÉKES-e
// helyzetjelentés. Korábban ez a logika csak az AdopterPortalPage-ben élt (a
// típusválasztó legördülő feltöltéséhez), így semmi nem tudta megmondani, hogy
// egy állat KÉSIK-e — sem a "Saját Állataim" lista, sem az emlékeztető.
//
// A mérföldkövek a menhelyenként állítható `shelters.followup_schedule`-ből
// jönnek (l. 008_followup_schedule.sql); az itteni alapértelmezés ugyanaz a
// 3 nap / 3 hét / 3 hónap hármas, és a `post_adoption_updates.update_type`
// CHECK megszorításához igazodik — új mérföldkő csak a séma bővítésével jöhet.
// ===========================================================================

import type { Animal } from './types';

/** Hány nappal a mérföldkő ELŐTT nyílik meg a jelentés beküldése. */
export const CHECKIN_WINDOW_DAYS = 5;

/**
 * Hány nappal a mérföldkő után számít KÉSETTNEK a jelentés. A mérföldkő és
 * e között az állapot még "esedékes" — az örökbefogadónak van néhány napja
 * fotót készíteni, mielőtt piros riasztást kapna.
 */
export const CHECKIN_OVERDUE_AFTER_DAYS = 2;

/**
 * Meddig marad "időszerű" egy elmulasztott mérföldkő. Enélkül egy fél éve
 * kihagyott 3 napos jelentés örökre pirosan világítana a listában, és — mivel
 * a legrégebben lejárt mérföldkő a legsürgetőbb — folyamatosan elfedné a
 * ténylegesen aktuális teendőt. Az ennél régebbi mulasztás már nem az
 * örökbefogadó felületén megoldandó ügy: arról a menhely kap értesítést
 * (l. send-checkin-reminders / check-missed-checkins).
 */
export const CHECKIN_OVERDUE_RELEVANT_DAYS = 30;

export interface CheckInMilestone {
  /** `post_adoption_updates.update_type` érték. */
  value: string;
  label: string;
  /** Napok az örökbefogadás óta. */
  days: number;
}

export const CHECKIN_MILESTONES: CheckInMilestone[] = [
  { value: '3_Day', label: '3 napos', days: 3 },
  { value: '3_Week', label: '3 hetes', days: 21 },
  { value: '3_Month', label: '3 hónapos', days: 90 },
];

export interface CheckInSlot extends CheckInMilestone {
  targetDate: Date;
}

/**
 * Egy állat helyzetjelentési állapota.
 *
 * - `none`     — nincs most teendő (nincs örökbefogadási dátum, vagy minden
 *                esedékes mérföldkő teljesítve, vagy a következő még messze)
 * - `upcoming` — a beküldési ablak megnyílt, de a mérföldkő még nem jött el
 * - `due`      — a mérföldkő elérkezett, a jelentés MOST időszerű
 * - `overdue`  — a mérföldkő elmúlt, a jelentés hiányzik
 */
export type CheckInUrgency = 'none' | 'upcoming' | 'due' | 'overdue';

export interface CheckInStatus {
  urgency: CheckInUrgency;
  /** A legsürgetőbb, még teljesítetlen mérföldkő (ha van). */
  milestone: CheckInMilestone | null;
  /** Napok a mérföldkőig; negatív = ennyi napja lejárt. */
  daysUntilDue: number | null;
  /** `urgency === 'due' | 'overdue'` — ekkor kell kiemelni a felületen. */
  needsAttention: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Nap pontosságú különbség — az óra/perc eltérés sose billentsen át egy napot. */
const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** A mérföldkövek dátumra váltva egy adott örökbefogadási dátumhoz. */
export function getCheckInSlots(adoptionDate: string | null | undefined): CheckInSlot[] {
  if (!adoptionDate) return [];
  const adoption = new Date(adoptionDate);
  if (Number.isNaN(adoption.getTime())) return [];
  return CHECKIN_MILESTONES.map(m => ({
    ...m,
    targetDate: new Date(adoption.getTime() + m.days * DAY_MS),
  }));
}

/**
 * Azok a mérföldkövek, amelyek beküldési ablaka NYITVA van (a mérföldkő
 * ±CHECKIN_WINDOW_DAYS környezetében), a már teljesítettek nélkül. Ez tölti
 * fel a helyzetjelentés típusválasztóját.
 */
export function getAvailableCheckInSlots(
  animal: Pick<Animal, 'adoptionDate' | 'postAdoptionUpdates'>,
  now: Date = new Date()
): CheckInSlot[] {
  const done = submittedTypes(animal);
  const today = startOfDay(now);
  return getCheckInSlots(animal.adoptionDate)
    .filter(s => !done.has(s.value))
    .filter(s => Math.abs(today - startOfDay(s.targetDate)) <= CHECKIN_WINDOW_DAYS * DAY_MS)
    .sort((a, b) => a.targetDate.getTime() - b.targetDate.getTime());
}

/** Amely mérföldkövekhez már érkezett jelentés. */
function submittedTypes(animal: Pick<Animal, 'postAdoptionUpdates'>): Set<string> {
  return new Set((animal.postAdoptionUpdates || []).map(u => u.updateType).filter(Boolean));
}

/**
 * Egy állat legsürgetőbb helyzetjelentési állapota.
 *
 * A "legsürgetőbb" a legrégebben lejárt, még teljesítetlen mérföldkő — így
 * egy régóta elmaradt 3 napos jelentés nem tűnik el egy épp közelgő 3 hetes
 * mögött. Csak `Adopted` állatokat vizsgálunk: a folyamatban lévő
 * örökbefogadásnál még nincs mit jelenteni.
 */
export function getCheckInStatus(
  animal: Pick<Animal, 'status' | 'adoptionDate' | 'postAdoptionUpdates'>,
  now: Date = new Date()
): CheckInStatus {
  const idle: CheckInStatus = { urgency: 'none', milestone: null, daysUntilDue: null, needsAttention: false };
  if (animal.status !== 'Adopted' || !animal.adoptionDate) return idle;

  const done = submittedTypes(animal);
  const today = startOfDay(now);

  const pending = getCheckInSlots(animal.adoptionDate)
    .filter(s => !done.has(s.value))
    .map(s => ({ slot: s, daysUntilDue: Math.round((startOfDay(s.targetDate) - today) / DAY_MS) }))
    // Amelyiknek még a beküldési ablaka sem nyílt meg, azzal most nincs dolgunk,
    // ahogy a rég lejárt, már nem behozható mérföldkővel sem.
    .filter(x => x.daysUntilDue <= CHECKIN_WINDOW_DAYS && x.daysUntilDue >= -CHECKIN_OVERDUE_RELEVANT_DAYS)
    // Legsürgetőbb elöl: a legkisebb (legnegatívabb) hátralévő nap.
    .sort((a, b) => a.daysUntilDue - b.daysUntilDue);

  const next = pending[0];
  if (!next) return idle;

  const urgency: CheckInUrgency =
    next.daysUntilDue < -CHECKIN_OVERDUE_AFTER_DAYS ? 'overdue'
      : next.daysUntilDue <= 0 ? 'due'
        : 'upcoming';

  return {
    urgency,
    milestone: next.slot,
    daysUntilDue: next.daysUntilDue,
    needsAttention: urgency === 'due' || urgency === 'overdue',
  };
}

/**
 * Rendezés a "Saját Állataim" listához: elöl azok, ahol időszerű a
 * helyzetjelentés (a késettek legelöl), utánuk a közelgők, végül a többi.
 * Azonos sürgősségen belül a friss örökbefogadás kerül előre.
 */
export function sortByCheckInUrgency<T extends Pick<Animal, 'status' | 'adoptionDate' | 'postAdoptionUpdates'>>(
  animals: T[],
  now: Date = new Date()
): T[] {
  const rank: Record<CheckInUrgency, number> = { overdue: 0, due: 1, upcoming: 2, none: 3 };
  return [...animals].sort((a, b) => {
    const sa = getCheckInStatus(a, now);
    const sb = getCheckInStatus(b, now);
    if (rank[sa.urgency] !== rank[sb.urgency]) return rank[sa.urgency] - rank[sb.urgency];
    // Azonos kategórián belül a sürgetőbb (kisebb hátralévő nap) elöl.
    if (sa.daysUntilDue !== null && sb.daysUntilDue !== null && sa.daysUntilDue !== sb.daysUntilDue) {
      return sa.daysUntilDue - sb.daysUntilDue;
    }
    return (b.adoptionDate || '').localeCompare(a.adoptionDate || '');
  });
}

/** Rövid, ember által olvasható állapotcímke a kártyákhoz/badge-ekhez. */
export function checkInStatusLabel(status: CheckInStatus): string | null {
  if (!status.milestone || status.daysUntilDue === null) return null;
  const { label } = status.milestone;
  switch (status.urgency) {
    case 'overdue':
      return `${label} jelentés ${Math.abs(status.daysUntilDue)} napja esedékes`;
    case 'due':
      return status.daysUntilDue === 0 ? `${label} jelentés ma esedékes` : `${label} jelentés esedékes`;
    case 'upcoming':
      return `${label} jelentés ${status.daysUntilDue} nap múlva`;
    default:
      return null;
  }
}
